import { ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useChampProfile } from "@/hooks/use-champs";
import { useCustomerProfile } from "@/hooks/use-customers";
import { navItemForPath } from "./nav-items";

const CUSTOMER_PATH = /^\/customers\/(\d+)\/?$/;
const CHAMP_PATH = /^\/champs\/(\d+)\/?$/;

/**
 * "Customers › Priya Sharma" on the full customer page. There is no customers
 * list to link back to, so "Customers" is plain text; the name comes from the
 * same query the page already runs.
 */
function CustomerCrumb({ id }: { id: number }) {
  const profile = useCustomerProfile(id);
  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-1.5 text-sm">
        <li className="flex shrink-0 items-center gap-1.5 text-muted-foreground max-sm:hidden">
          Customers
          <ChevronRight className="size-3.5" aria-hidden="true" />
        </li>
        <li className="min-w-0 truncate">
          <span aria-current="page" className="font-medium text-foreground">
            {profile.data?.name ?? `CX-${id}`}
          </span>
        </li>
      </ol>
    </nav>
  );
}

/** "Champs › Ravi Kumar" on the full champ page; the name comes from the page's own query. */
function ChampCrumb({ id }: { id: number }) {
  const profile = useChampProfile(id);
  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-1.5 text-sm">
        <li className="flex shrink-0 items-center gap-1.5 text-muted-foreground max-sm:hidden">
          Champs
          <ChevronRight className="size-3.5" aria-hidden="true" />
        </li>
        <li className="min-w-0 truncate">
          <span aria-current="page" className="font-medium text-foreground">
            {profile.data?.name ?? `CH-${id}`}
          </span>
        </li>
      </ol>
    </nav>
  );
}

/**
 * "Calls › Call logs" in the top bar — where you are, in the sidebar's own
 * terms. Derived from the nav items rather than declared per page, so the
 * crumb and the highlighted link can never disagree.
 *
 * On a page below a screen (`/tasks/1001`) the screen's name becomes a link
 * back to it; the record itself is named by the page's own h1.
 */
export function Breadcrumbs() {
  const { pathname } = useLocation();
  const customerMatch = CUSTOMER_PATH.exec(pathname);
  if (customerMatch) return <CustomerCrumb id={Number(customerMatch[1])} />;
  const champMatch = CHAMP_PATH.exec(pathname);
  if (champMatch) return <ChampCrumb id={Number(champMatch[1])} />;
  const item = navItemForPath(pathname);
  if (item === undefined) return null;

  const isScreen = pathname === item.to;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex items-center gap-1.5 text-sm">
        {item.group ? (
          <li className="flex shrink-0 items-center gap-1.5 text-muted-foreground max-sm:hidden">
            {item.group}
            <ChevronRight className="size-3.5" aria-hidden="true" />
          </li>
        ) : null}
        <li className="min-w-0 truncate">
          {isScreen ? (
            <span aria-current="page" className="font-medium text-foreground">
              {item.label}
            </span>
          ) : (
            <Link
              to={item.to}
              className="rounded text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {item.label}
            </Link>
          )}
        </li>
      </ol>
    </nav>
  );
}
